/** @jsx jsx */
import { css, jsx, keyframes } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

import times from 'lodash/times';

import { color, placeholder } from '@coursera/coursera-ui';

import BrowseCarousel from 'bundles/browse-common/legacy/components/BrowseCarousel';
import { BrowseProductCardPlaceholder } from 'bundles/browse/components/ProductCard/BrowseProductCard';
import { SCREEN_TABLET, responsiveProperty } from 'bundles/browse/constants';

const shimmerAnimationDuration = 1.3;
const shimmerKeyframes = keyframes`
  '0%' {
    backgroundPosition: -468px 0;
  },
  '100%' {
    backgroundPosition: 468px 0;
  },
`;

export const CollectionStyle = css`
  .rc-Collection,
  &.rc-Collection {
    position: relative;
    width: 100%;
    max-width: 1364px;
    margin: 0 auto;
    padding: 60px 0;

    @media (max-width: 1023px) {
      padding: 60px 0 0;
    }

    &.placeholder {
      overflow: hidden;
    }

    .collection-name {
      margin-bottom: 20px;
    }

    .rc-CollectionItem-wrapper {
      display: flex;
      justify-content: center;
      width: fit-content !important;
      padding: 0 12px;
    }

    .rc-BrowseProductCard {
      margin-right: 0;
    }

    .collection-link {
      color: var(--cds-color-neutral-primary);
    }

    .rc-BrowseCarousel {
      .slick-slide {
        min-width: 280px;
      }

      .unslicked .slick-slide {
        width: auto !important;
      }

      .slick-list {
        min-width: 100%;
      }
    }
  }
`;

const styles = {
  collectionNamePlaceholder: css`
    width: 400;
    height: 32;
    background-color: ${color.gray};
    animation-delay: ${placeholder.animationDuration / 4}s;

    @media (max-width: ${SCREEN_TABLET}px) {
      width: 240;
    }

    animation-name: ${shimmerKeyframes};
    animation-duration: ${shimmerAnimationDuration}s;
    animation-iteration-count: infinite;
    animation-fill-mode: forwards;
    animation-timing-function: linear;
    background: linear-gradient(
      to right,
      var(--cds-color-grey-25) 8%,
      var(--cds-color-grey-25) 18%,
      var(--cds-color-grey-25) 33%
    );
    background-size: 1000px 104px;
    position: relative;
    overflow: hidden;
  `,

  condensedStyle: css`
    padding: 50px 0 !important;

    .rc-BrowseProductCard {
      width: 236px !important;
      min-width: unset !important;
      height: 300px !important;

      & > div {
        height: 300px !important;
      }
    }
  `,
};

type Props = {
  collectionItemHeight?: number;
  hideCollectionNameHeader?: boolean;
  styleAdditions?: SerializedStyles[];
  numberOfPlaceholders?: number;
};

/** @deprecated replaced by the new ProductCardCollection, do not use this component at all cost */
const Placeholder = ({
  collectionItemHeight,
  hideCollectionNameHeader,
  styleAdditions,
  numberOfPlaceholders,
}: Props) => {
  const style = collectionItemHeight ? { height: collectionItemHeight } : {};
  const sectionStyle = styleAdditions ? [CollectionStyle, ...styleAdditions] : CollectionStyle;
  return (
    <section className="rc-Collection placeholder" css={sectionStyle} data-testid="CollectionPlaceholder">
      {!hideCollectionNameHeader && <div className="collection-name" css={styles.collectionNamePlaceholder} />}
      <BrowseCarousel style={style} responsive={responsiveProperty} slidesToShow={4} slidesToScroll={4}>
        {times(numberOfPlaceholders ?? 4, (i) => {
          return (
            <div className="rc-CollectionItem-wrapper" key={`placeholder-${i}`}>
              <BrowseProductCardPlaceholder />
            </div>
          );
        })}
      </BrowseCarousel>
    </section>
  );
};

/** @deprecated replaced by the new ProductCardCollection, do not use this component at all cost */
export default Placeholder;
